import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Plus, Search, Loader2 } from "lucide-react";
import { stageLabel } from "../../lib/stageLabels";

const GRADE_COLOR = { A: "text-emerald-400 border-emerald-500/40", B: "text-amber-400 border-amber-500/40", C: "text-rose-400 border-rose-500/40" };

export default function Prospects() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      const params = q.trim() ? { q: q.trim() } : {};
      api.get("/prospects", { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
    }, 250); // debounce so typing doesn't hammer the API
    return () => clearTimeout(t);
  }, [q]);

  const filtered = items || [];

  return (
    <div data-testid="prospects-list">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="tick-label text-sky-400 mb-2">/ Prospect Intelligence</div>
          <h1 className="font-display font-black text-3xl text-slate-100">All Prospects</h1>
        </div>
        <button
          onClick={() => nav("/internal/prospects/new")}
          data-testid="prospect-new-btn"
          className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
        >
          <Plus className="w-4 h-4" /> New Prospect
        </button>
      </div>

      <div className="relative mb-5 max-w-md">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company, contact, email, website, notes, certs, NAICS, UEI, CAGE..." className="w-full bg-[var(--bg-secondary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm pl-10 pr-3 py-2.5 font-mono" data-testid="prospects-search" />
      </div>

      {items === null && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-sky-400 animate-spin" /></div>}

      {items && items.length === 0 && (
        <div className="panel p-12 text-center">
          <div className="text-slate-300 mb-2">No prospects yet</div>
          <p className="text-sm text-slate-500">Click <span className="text-sky-400">New Prospect</span> to start.</p>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="panel">
          <div className="grid grid-cols-12 gap-3 px-4 py-2.5 border-b border-[var(--border-soft)] tick-label">
            <div className="col-span-4">Company</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-2">Stage</div>
            <div className="col-span-1 text-right">Score</div>
            <div className="col-span-1 text-center">Grade</div>
            <div className="col-span-2 text-right">Next Follow-up</div>
          </div>
          {filtered.map((p) => (
            <Link key={p.prospect_id} to={`/internal/prospects/${p.prospect_id}`} data-testid={`prospect-row-${p.prospect_id}`} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--bg-secondary)]">
              <div className="col-span-4">
                <div className="text-slate-100 text-sm">{p.company_name}</div>
                <div className="font-mono text-[10px] text-slate-500">{p.primary_naics || "—"}</div>
              </div>
              <div className="col-span-2 font-mono text-[11px] text-slate-300">{p.source_type || "—"}</div>
              <div className="col-span-2 font-mono text-[10px] tracking-widest uppercase text-sky-400">{stageLabel(p.stage)}</div>
              <div className="col-span-1 text-right metric-number text-sky-400">{p.score ?? "—"}</div>
              <div className="col-span-1 text-center">
                {p.grade ? <span className={`font-mono text-[10px] tracking-widest uppercase px-1.5 py-0.5 border ${GRADE_COLOR[p.grade]}`}>{p.grade}</span> : <span className="text-slate-600">—</span>}
              </div>
              <div className="col-span-2 font-mono text-[10px] text-slate-400 text-right">{(p.next_follow_up_date || "").slice(0, 10) || "—"}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
